import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Bot, Wifi, WifiOff } from 'lucide-react';
import { Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { AgentStatus, AgentType } from './types.js';

const { Text } = Typography;

export interface WorkforceAgent {
  id?: string;
  name: string;
  status: AgentStatus;
  type?: AgentType;
  currentProject?: string;
}

export interface AgentStatusWidgetProps {
  agents?: readonly WorkforceAgent[];
  className?: string;
  testId?: string;
}

export function AgentStatusWidget({
  agents = [],
  className,
  testId,
}: AgentStatusWidgetProps): ReactElement {
  const { t } = useTranslation();
  const runningCount = agents.filter(
    (agent) => agent.status === 'running' || agent.status === 'active',
  ).length;

  const widgetContainerProps = {
    title: t('crm.dashboard.workforce.title'),
    icon: Bot,
    minHeight: 260,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span
        style={{
          fontFamily: 'monospace',
          fontSize: 11,
          fontWeight: 600,
          color: runningCount > 0 ? 'var(--ant-color-success)' : 'var(--ant-color-text-tertiary)',
        }}
      >
        {t('crm.dashboard.workforce.active_count', { count: runningCount })}
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {agents.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <Bot size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.workforce.empty')}
          </span>
        </div>
      ) : (
        /* Wide panel: signals read as a two-column hairline matrix (the same
           gap-px trick as the KPI strip), collapsing to one column on mobile. */
        <ul
          className="m-0 grid list-none grid-cols-1 gap-px p-0 md:grid-cols-2"
          style={{ background: 'var(--ant-color-border-secondary, #e7e5e4)' }}
        >
          {agents.map((agent, index) => {
            const isActive = agent.status === 'active' || agent.status === 'running';

            return (
              <li
                key={agent.id ?? `${agent.name}-${index}`}
                className="crm-hover-fill flex items-center justify-between"
                style={{
                  padding: '8px 14px',
                  background: 'var(--ant-color-bg-container, #fffdfa)',
                  transition: 'background-color 110ms ease',
                }}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="crm-radius-s flex shrink-0 items-center justify-center"
                    style={{
                      width: 30,
                      height: 30,
                      background: isActive ? 'var(--ant-color-success-bg)' : 'var(--g-fill)',
                      border: `1px solid ${isActive ? 'var(--ant-color-success-border)' : 'var(--ant-color-border-secondary)'}`,
                    }}
                  >
                    <Bot
                      size={13}
                      style={{
                        color: isActive
                          ? 'var(--ant-color-success)'
                          : 'var(--ant-color-text-tertiary)',
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <Text
                      strong
                      className="block truncate"
                      style={{
                        fontSize: 12.5,
                        color: 'var(--ant-color-text)',
                        lineHeight: 1.2,
                      }}
                    >
                      {agent.name}
                    </Text>
                    <Text
                      type="secondary"
                      className="block truncate"
                      style={{ fontSize: 10, marginTop: 2 }}
                    >
                      {agent.type
                        ? t(`crm.dashboard.workforce.type.${agent.type}`)
                        : t('crm.dashboard.workforce.type.agent')}
                      {agent.currentProject ? ` · ${agent.currentProject}` : ''}
                    </Text>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {isActive ? (
                    <Wifi size={12} style={{ color: 'var(--ant-color-success)' }} />
                  ) : (
                    <WifiOff
                      size={12}
                      style={{ color: 'var(--ant-color-text-tertiary)', opacity: 0.4 }}
                    />
                  )}
                  <div
                    className={isActive ? 'crm-dot-pulse' : undefined}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: isActive
                        ? 'var(--ant-color-success)'
                        : 'var(--ant-color-border)',
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetContainer>
  );
}
